import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ChatMessageEvent } from 'events/types';
import {
  registerChatMessageCallback,
  unregisterChatMessageCallback,
} from 'events/webSocketHandlers';
import { normalizeBackendUtcIsoString } from 'utils/dateUtils';
import { useSnackbar } from 'components/GlobalSnackbar';
import type { Message } from '../api/model';

interface StreamingMessage {
  role: string;
  content: string;
  created_at?: string;
  isStreaming?: boolean;
  progressLabel?: string | null;
  progressTokens?: number | null;
}

export interface UseStreamingMessageResult {
  streamingMessage: StreamingMessage | null;
  displayedContent: string;
  currentTaskId: string | null;
  progressLabel: string | null;
  progressTokens: number | null;
  resetStreaming: () => void;
  handleStreamComplete: () => void;
}

export const useStreamingMessage = (
  chat: Message[],
  setChat: React.Dispatch<React.SetStateAction<Message[]>>,
  setLoading: (loading: boolean) => void
): UseStreamingMessageResult => {
  const showSnackbar = useSnackbar();
  const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null);
  const [targetContent, setTargetContent] = useState('');
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [finalTimestamp, setFinalTimestamp] = useState<string>('');
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [progressTokens, setProgressTokens] = useState<number | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const localStorageTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [currentTaskId, setCurrentTaskId] = useState<string | null>(() =>
    localStorage.getItem('chat_task_id')
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentTaskId(localStorage.getItem('chat_task_id'));
    };
    const interval = setInterval(handleStorageChange, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentTaskId) {
      const streamingMessageStr = localStorage.getItem(`stream_chat_${currentTaskId}`);
      if (streamingMessageStr) {
        const parsed = JSON.parse(streamingMessageStr) as StreamingMessage & {
          content?: string;
          progressLabel?: string | null;
          progressTokens?: number | null;
        };
        const lastMessage = chat[chat.length - 1];
        if (
          lastMessage &&
          lastMessage.role === 'AI' &&
          lastMessage.content === parsed.content &&
          lastMessage.created_at
        ) {
          localStorage.removeItem(`stream_chat_${currentTaskId}`);
          localStorage.removeItem('chat_task_id');
          setStreamingMessage(null);
          setTargetContent('');
          setDisplayedContent('');
          setLoading(false);
        } else if (!streamingMessage) {
          setStreamingMessage(parsed);
          if (parsed?.content) {
            setTargetContent(parsed.content);
            setDisplayedContent(parsed.content);
          }
          if (parsed?.progressLabel) {
            setProgressLabel(parsed.progressLabel);
          }
          if (parsed?.progressTokens != null) {
            setProgressTokens(parsed.progressTokens);
          }
        }
      }
    }
  }, [currentTaskId, chat, setLoading, streamingMessage]);

  useEffect(() => {
    if (!targetContent) {
      setDisplayedContent('');
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    if (displayedContent.length < targetContent.length) {
      const CHARS_PER_INTERVAL = 50;
      const INTERVAL_MS = 8;

      const animate = (currentTime: number) => {
        if (currentTime - lastUpdateTimeRef.current >= INTERVAL_MS) {
          lastUpdateTimeRef.current = currentTime;
          setDisplayedContent((prev) => {
            const nextLength = Math.min(prev.length + CHARS_PER_INTERVAL, targetContent.length);
            return targetContent.slice(0, nextLength);
          });
        }
        if (displayedContent.length < targetContent.length) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    } else if (isComplete && displayedContent.length === targetContent.length && targetContent) {
      setChat((prevChat) => [
        ...prevChat,
        {
          role: 'AI',
          content: targetContent,
          created_at: finalTimestamp,
        },
      ]);
      setStreamingMessage(null);
      setTargetContent('');
      setDisplayedContent('');
      localStorage.removeItem('chat_task_id');
      setIsComplete(false);
      setFinalTimestamp('');
      setLoading(false);
    }
  }, [targetContent, displayedContent, isComplete, finalTimestamp, setChat, setLoading]);

  const saveToLocalStorage = useCallback((task_id: string, message: StreamingMessage) => {
    if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
    localStorageTimerRef.current = setTimeout(() => {
      localStorage.setItem(`stream_chat_${task_id}`, JSON.stringify(message));
    }, 100);
  }, []);

  const handleChatMessage = useCallback(
    (data: ChatMessageEvent & { created_at?: string; error_message?: string }) => {
      const { task_id, status, content, created_at, progress, error_message } = data;
      if (task_id !== currentTaskId) return;

      if (status === 'error') {
        // Сбрасываем весь стриминговый процесс — иначе UI остаётся в "ИИ печатает...".
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (localStorageTimerRef.current) {
          clearTimeout(localStorageTimerRef.current);
          localStorageTimerRef.current = null;
        }
        localStorage.removeItem(`stream_chat_${task_id}`);
        localStorage.removeItem('chat_task_id');
        setStreamingMessage(null);
        setTargetContent('');
        setDisplayedContent('');
        setIsComplete(false);
        setFinalTimestamp('');
        setProgressLabel(null);
        setProgressTokens(null);
        setLoading(false);
        showSnackbar({
          message: error_message
            ? `Ошибка ИИ: ${error_message}`
            : 'Ошибка ИИ при ответе на сообщение',
          severity: 'error',
          duration: 5000,
        });
        return;
      }

      if (status === 'stream') {
        setTargetContent(content ?? '');
        const newProgressLabel = progress?.label ?? null;
        const newProgressTokens = progress?.tokens ?? null;
        setProgressLabel(newProgressLabel);
        setProgressTokens(newProgressTokens);
        const msg: StreamingMessage = {
          role: 'AI',
          content: content ?? '',
          isStreaming: true,
          progressLabel: newProgressLabel,
          progressTokens: newProgressTokens,
        };
        setStreamingMessage(msg);
        saveToLocalStorage(task_id, msg);
      } else if (status === 'success') {
        setTargetContent(content ?? '');
        setFinalTimestamp(normalizeBackendUtcIsoString(created_at ?? new Date().toISOString()));
        setIsComplete(true);
        setProgressLabel(null);
        setProgressTokens(null);
        if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
        localStorage.removeItem(`stream_chat_${task_id}`);
        setStreamingMessage({
          role: 'AI',
          content: content ?? '',
          isStreaming: true,
        });
      }
    },
    [currentTaskId, saveToLocalStorage, setLoading, showSnackbar]
  );

  useEffect(() => {
    registerChatMessageCallback(handleChatMessage as (data: ChatMessageEvent) => void);
    return () => {
      unregisterChatMessageCallback();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
    };
  }, [handleChatMessage]);

  const resetStreaming = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (localStorageTimerRef.current) {
      clearTimeout(localStorageTimerRef.current);
      localStorageTimerRef.current = null;
    }
    if (currentTaskId) localStorage.removeItem(`stream_chat_${currentTaskId}`);
    setStreamingMessage(null);
    setTargetContent('');
    setDisplayedContent('');
    setIsComplete(false);
    setFinalTimestamp('');
    setProgressLabel(null);
    setProgressTokens(null);
  }, [currentTaskId]);

  const handleStreamComplete = useCallback(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (localStorageTimerRef.current) clearTimeout(localStorageTimerRef.current);
    setStreamingMessage(null);
    setTargetContent('');
    setDisplayedContent('');
    localStorage.removeItem('chat_task_id');
    if (currentTaskId) localStorage.removeItem(`stream_chat_${currentTaskId}`);
    setIsComplete(false);
    setFinalTimestamp('');
    setProgressLabel(null);
    setProgressTokens(null);
    setLoading(false);
  }, [currentTaskId, setLoading]);

  return {
    streamingMessage,
    displayedContent,
    currentTaskId,
    progressLabel,
    progressTokens,
    resetStreaming,
    handleStreamComplete,
  };
};
